import {useEffect, useRef, useState} from 'react'
import {useResizeObserver, type ResizeObserverHandler} from '@github-ui/use-resize-observer'

export function useStickyHeaderNavigation(enabled: boolean) {
  const boundaryRef = useRef<HTMLDivElement>(null)
  const navigationRef = useRef<HTMLDivElement>(null)
  const onResizeRef = useRef<ResizeObserverHandler | null>(null)
  const [showCompactNavigation, setShowCompactNavigation] = useState(false)
  const observeResize = useResizeObserver(entry => onResizeRef.current?.(entry))

  useEffect(() => {
    const boundaryElement = boundaryRef.current
    const navigationElement = navigationRef.current
    if (!enabled || !boundaryElement || !navigationElement) return

    const root = navigationElement.ownerDocument
    let boundary = 0
    let documentHeight = 0
    let observer: IntersectionObserver | undefined

    onResizeRef.current = entry => {
      const nextBoundary = entry.target === boundaryElement ? entry.contentRect.height : boundary
      const nextDocumentHeight =
        entry.target === root.documentElement ? root.documentElement.scrollHeight : documentHeight
      if (nextBoundary === boundary && nextDocumentHeight === documentHeight) return
      boundary = nextBoundary
      documentHeight = nextDocumentHeight
      if (!boundary || !documentHeight) return

      observer?.disconnect()
      observer = new IntersectionObserver(
        entries => {
          const intersection = entries[entries.length - 1]
          if (!intersection) return
          // Non-intersection alone also includes tabs that are still below the viewport.
          const aboveBoundary =
            !!intersection.rootBounds && intersection.boundingClientRect.bottom < intersection.rootBounds.top
          setShowCompactNavigation(previous => (previous === aboveBoundary ? previous : aboveBoundary))
        },
        {
          root,
          // Keep tabs below the viewport intersecting so direct scroll jumps cross a threshold.
          // Document growth updates the observer, not the React layout.
          rootMargin: `-${boundary}px 0px ${documentHeight}px 0px`,
          threshold: 0,
        },
      )
      observer.observe(navigationElement)
    }

    const stopBoundary = observeResize(boundaryElement)
    const stopDocument = observeResize(root.documentElement)

    return () => {
      onResizeRef.current = null
      stopBoundary?.()
      stopDocument?.()
      observer?.disconnect()
    }
  }, [enabled, observeResize])

  return {boundaryRef, navigationRef, showCompactNavigation: enabled && showCompactNavigation}
}
